import { type ComputedMarker } from "@/shared/types/zod";
import { firstProgress } from "@/utils/markerProgress";

const FADE_IN_DURATION = 200;
const FADE_OUT_DURATION = 150;
const CLEANUP_INTERVAL = 1000;

/** A single 0..1 value that eases toward a target (0 or 1) with separate fade-in/out durations. */
interface AnimatedValue {
	value: number;
	target: number;
	startValue: number;
	startTime: number | undefined;
}

function createAnimatedValue(): AnimatedValue {
	return { value: 0, target: 0, startValue: 0, startTime: undefined };
}

/** Advance an animated value toward `target` (0 or 1). Returns true while still animating. */
function animate(av: AnimatedValue, target: 0 | 1, now: number): boolean {
	if (av.target !== target) {
		av.startValue = av.value;
		av.startTime = now;
		av.target = target;
	}

	if (av.value === target) {
		av.startTime = undefined;
		return false;
	}

	if (av.startTime === undefined) {
		av.value = target;
		return false;
	}

	const duration = target === 1 ? FADE_IN_DURATION : FADE_OUT_DURATION;
	const progress = Math.min(1, (now - av.startTime) / duration);
	av.value = av.startValue + (target - av.startValue) * progress;

	if (progress >= 1) {
		av.value = target;
		av.startValue = target;
		av.startTime = undefined;
		return false;
	}

	return true;
}

/** Icon animation for a marker, plus whether its label is eligible to be shown this frame. */
export interface MarkerAnimationSnapshot {
	iconScale: number;
	/**
	 * Whether the marker is within its zoom range this frame, independent of whether the route
	 * animation has revealed it yet. Used to reserve collision space for markers (and their
	 * labels) that the animation hasn't reached, so visible labels don't reflow as upcoming
	 * markers appear. Markers hidden purely by zoom are never within zoom and reserve nothing.
	 */
	withinZoom: boolean;
	isAnimating: boolean;
	/** Zoom + text-visibility gate. The label is revealed (drawn) this frame, subject to overlap. */
	labelEligible: boolean;
}

/** Independent label animation, driven by the final (eligible + placed) visibility decision. */
export interface MarkerLabelAnimation {
	opacity: number;
	isAnimating: boolean;
}

interface MarkerAnimationContext {
	zoom: number;
	ignoreZoom?: boolean;
	markerVisibility: number;
	textVisibility: number;
	now: number;
}

interface MarkerAnimationState {
	icon: AnimatedValue;
	label: AnimatedValue;
}

function createInitialState(): MarkerAnimationState {
	return {
		icon: createAnimatedValue(),
		label: createAnimatedValue(),
	};
}

export class MarkerAnimationController {
	private readonly states = new Map<string, MarkerAnimationState>();
	private lastCleanup = 0;

	/** Update the marker icon animation and compute whether its label is eligible to show. */
	update(marker: ComputedMarker, context: MarkerAnimationContext): MarkerAnimationSnapshot {
		const state = this.getState(marker.id);
		const minZoom = marker.properties.zoom ?? 0;
		const progress = firstProgress(marker.properties.__progress, 0);

		// Two independent reasons a marker may be hidden: its zoom range, and the route
		// animation not having reached it yet. Keep them separate so animation-hidden markers
		// can still reserve collision space while zoom-hidden ones do not.
		const withinZoom = context.ignoreZoom || context.zoom >= minZoom;
		const reachedByAnimation = context.markerVisibility >= progress;
		const iconVisible = withinZoom && reachedByAnimation;
		const iconTarget = iconVisible ? 1 : 0;
		const labelEligible = iconVisible && context.textVisibility > 0 && context.textVisibility >= progress;

		const isAnimating = animate(state.icon, iconTarget, context.now);

		return {
			iconScale: state.icon.value,
			withinZoom,
			isAnimating,
			labelEligible,
		};
	}

	/** Update the label animation toward `visible` (eligible AND placed without overlap). */
	updateLabel(id: string, visible: boolean, now: number): MarkerLabelAnimation {
		const state = this.getState(id);
		const isAnimating = animate(state.label, visible ? 1 : 0, now);
		return { opacity: state.label.value, isAnimating };
	}

	cleanup(activeIds: Iterable<string>, now: number) {
		if (now - this.lastCleanup <= CLEANUP_INTERVAL) return;
		this.lastCleanup = now;

		const activeIdSet = activeIds instanceof Set ? activeIds : new Set(activeIds);
		for (const [id, state] of this.states) {
			if (!activeIdSet.has(id) && state.icon.value === 0 && state.label.value === 0) {
				this.states.delete(id);
			}
		}
	}

	private getState(id: string): MarkerAnimationState {
		let state = this.states.get(id);
		if (!state) {
			state = createInitialState();
			this.states.set(id, state);
		}
		return state;
	}
}